import {inject, Type} from '@angular/core';
import {CpmCKeycloakApiService} from '../../gen/cpm-c-api-ts/src/services/c-global/cpmc-keycloak.api-service';
import {CpmCKeycloakConfigApiSchema} from '../../gen/cpm-c-api-ts/src/schemas/c-global/cpmc-keycloak.api-schemas';
import {Observable} from 'rxjs';
import {SessionService} from './services/session.service';
import {ECpmCRouteTypeApiEnum} from '../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {BaseCpmCApiModel} from '../../gen/cpm-c-api-ts/src/models/cpmc-base.api-model';
import {CustomerUrlBuilderUtil} from './utils/customer-url-builder.util';
import {IDataCellDisplayComponent} from '../../sdk/src/data-cell-display/idata-cell-display/idata-cell-display.component';
import {UserDataCellDisplayComponent} from './data-cell-display/user-data-cell-display/user-data-cell-display.component';

export type SdkBridgeBaseModel = BaseCpmCApiModel;

/**
 * Bridge between the abstract SDK and the app implementation
 */
export class SdkBridge {
    private static sessionService: SessionService;

    // ===============================================================
    // ===============================================================

    public static multiSchemaPrefix = 'customer';
    public static multiSchemaModelIdentifierField = `${SdkBridge.multiSchemaPrefix}_uuid`;

    public static readonly userDataCellDisplayComponent: Type<IDataCellDisplayComponent<unknown, unknown>> =
        UserDataCellDisplayComponent;

    // ===============================================================
    // ===============================================================

    /**
     * Inject required services, called from app.component constructor
     */
    public static _inject() {
        SdkBridge.sessionService = inject(SessionService);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get the page not found URL
     * @returns URL of the not found page
     */
    public static pageNotFoundUrl(): string {
        return CustomerUrlBuilderUtil.notFound();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Fetch the keycloak configuration
     * @returns KeycloakConfigSchema observable
     */
    public static fetchKeycloakConfig(): Observable<CpmCKeycloakConfigApiSchema> {
        return CpmCKeycloakApiService.getConfig();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Are we currently in a multi schema?
     * @returns True or false
     */
    public static async isInMultiSchema(): Promise<boolean> {
        await SdkBridge.sessionService.getRouteContextIsReadyPromise();
        return SdkBridge.sessionService.getCustomer() !== undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Is current user allowed for the @routeResourceEndpoint with type READ
     * @param routeResourceEndpoint Route resource endpoint
     * @returns True or false
     */
    public static isAuthorizedForRouteResourceRead(routeResourceEndpoint: string): boolean {
        return SdkBridge.sessionService.isAuthorizedForRouteResource(routeResourceEndpoint, ECpmCRouteTypeApiEnum.READ);
    }
    /**
     * Is current user allowed for the @routeResourceEndpoint with type CREATE
     * @param routeResourceEndpoint Route resource endpoint
     * @returns True or false
     */
    public static isAuthorizedForRouteResourceCreate(routeResourceEndpoint: string): boolean {
        return SdkBridge.sessionService.isAuthorizedForRouteResource(
            routeResourceEndpoint,
            ECpmCRouteTypeApiEnum.CREATE
        );
    }
    /**
     * Is current user allowed for the @routeResourceEndpoint with type UPDATE
     * @param routeResourceEndpoint Route resource endpoint
     * @returns True or false
     */
    public static isAuthorizedForRouteResourceUpdate(routeResourceEndpoint: string): boolean {
        return SdkBridge.sessionService.isAuthorizedForRouteResource(
            routeResourceEndpoint,
            ECpmCRouteTypeApiEnum.UPDATE
        );
    }
    /**
     * Is current user allowed for the @routeResourceEndpoint with type DELETE
     * @param routeResourceEndpoint Route resource endpoint
     * @returns True or false
     */
    public static isAuthorizedForRouteResourceDelete(routeResourceEndpoint: string): boolean {
        return SdkBridge.sessionService.isAuthorizedForRouteResource(
            routeResourceEndpoint,
            ECpmCRouteTypeApiEnum.DELETE
        );
    }
}
